import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useWeb3V3 } from '../contexts/Web3ContextV3';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * V3 Navigation Bar Component - Black & White Premium Theme
 * Navigation menu + Wallet connection + Balance display + Language switcher
 */
const NavbarV3 = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    account,
    balances,
    connectWallet,
    disconnectWallet,
    isConnecting
  } = useWeb3V3();

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Navigation menu items - Full 5 pages
  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/mint', label: t('nav.mintNFT') },
    { path: '/my-assets', label: t('nav.myAssets') },
    { path: '/draws', label: t('nav.drawResults') },
    { path: '/marketplace', label: t('nav.marketplace') }
  ];

  // Check if current path is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Format address
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <nav className="bg-black border-b-2 border-gray-800 sticky top-0 z-50">
      <div className="container-v3">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="font-black text-xl tracking-tight">{t('nav.nftLottery')}</div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 font-medium transition-all ${
                  isActive(item.path)
                    ? 'bg-white text-black'
                    : 'text-gray-400 hover:text-white hover:bg-gray-900'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right Section: Language + Wallet */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {account ? (
              <>
                {/* Balance Display */}
                <div className="flex items-center gap-4 px-4 py-2 bg-gray-950 border border-gray-800">
                  <div className="text-sm">
                    <div className="text-gray-500 text-xs">{t('common.usdt')}</div>
                    <div className="font-mono font-bold">
                      {parseFloat(balances.usdt).toFixed(2)}
                    </div>
                  </div>
                  <div className="w-px h-8 bg-gray-800"></div>
                  <div className="text-sm">
                    <div className="text-gray-500 text-xs">{t('common.eth')}</div>
                    <div className="font-mono font-bold">
                      {parseFloat(balances.eth).toFixed(4)}
                    </div>
                  </div>
                </div>

                {/* Account Display */}
                <div className="flex items-center gap-2">
                  <div className="px-4 py-2 bg-gray-950 border border-gray-800 font-mono text-sm">
                    {formatAddress(account)}
                  </div>
                  <button
                    onClick={disconnectWallet}
                    className="btn-ghost px-3 py-2 text-sm"
                    title={t('common.disconnect')}
                  >
                    ✕
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={connectWallet}
                disabled={isConnecting}
                className="btn-primary px-6 py-2"
              >
                {isConnecting ? t('common.connecting') : t('common.connectWallet')}
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 hover:bg-gray-900 transition-colors"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div id="mobile-navigation" className="md:hidden border-t border-gray-800 py-4">
            {/* Mobile Navigation Links */}
            <div className="space-y-2 mb-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 font-medium transition-all ${
                    isActive(item.path)
                      ? 'bg-white text-black'
                      : 'text-gray-400 hover:text-white hover:bg-gray-900'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Mobile Wallet Section */}
            <div className="border-t border-gray-800 pt-4">
              {account ? (
                <div className="space-y-3">
                  {/* Mobile Balance Display */}
                  <div className="grid grid-cols-2 gap-3 px-4">
                    <div className="p-3 bg-gray-950 border border-gray-800 text-center">
                      <div className="text-gray-500 text-xs mb-1">{t('common.usdt')}</div>
                      <div className="font-mono font-bold">
                        {parseFloat(balances.usdt).toFixed(2)}
                      </div>
                    </div>
                    <div className="p-3 bg-gray-950 border border-gray-800 text-center">
                      <div className="text-gray-500 text-xs mb-1">{t('common.eth')}</div>
                      <div className="font-mono font-bold">
                        {parseFloat(balances.eth).toFixed(4)}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Account Display */}
                  <div className="px-4">
                    <div className="p-3 bg-gray-950 border border-gray-800 font-mono text-sm text-center">
                      {formatAddress(account)}
                    </div>
                  </div>

                  {/* Disconnect Button */}
                  <div className="px-4">
                    <button
                      onClick={() => {
                        disconnectWallet();
                        setMobileMenuOpen(false);
                      }}
                      className="btn-outline w-full py-3"
                    >
                      {t('common.disconnect')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-4">
                  <button
                    onClick={() => {
                      connectWallet();
                      setMobileMenuOpen(false);
                    }}
                    disabled={isConnecting}
                    className="btn-primary w-full py-3"
                  >
                    {isConnecting ? t('common.connecting') : t('common.connectWallet')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavbarV3;
